import { z } from 'zod';

/**
 * Zod schema for FloorPlanUnit — a single PC/unit on the canvas.
 */
export const FloorPlanUnitSchema = z.object({
  inventoryUnitId: z.string().uuid(),
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  rotation: z.number().min(0).max(359).optional(),
});

/**
 * Zod schema for FloorPlanConfig — the full floor plan JSON stored on Venue.
 *
 * Validates:
 *   - dimensions.width/height are positive integers
 *   - gridSize is a positive number
 *   - units array has no duplicate inventoryUnitIds
 *   - unit positions are non-negative integers (canvas bounds not enforced here
 *     to avoid coupling schema to display size)
 */
export const FloorPlanConfigSchema = z
  .object({
    dimensions: z.object({
      width: z.number().int().min(1).max(10000),
      height: z.number().int().min(1).max(10000),
    }),
    gridSize: z.number().min(1).max(500),
    background: z.string().url().optional().nullable(),
    units: z.array(FloorPlanUnitSchema),
  })
  .superRefine((val, ctx) => {
    const ids = val.units.map((u) => u.inventoryUnitId);
    const seen = new Set<string>();
    for (let i = 0; i < ids.length; i++) {
      if (seen.has(ids[i])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate inventoryUnitId at index ${i}: ${ids[i]}`,
          path: ['units', i, 'inventoryUnitId'],
        });
      }
      seen.add(ids[i]);
    }
  });

export type FloorPlanConfigInput = z.infer<typeof FloorPlanConfigSchema>;
